/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import {
  Col, Row, Typography, Button, TableColumnsType,
  Tooltip,
  Button as ButtonAnt,
  Table,
  App
} from 'antd';
import { ListPhysicalActivitiesFilter } from '@/components/forms/list-physical-activities.filter.form';
import { PlusOutlined } from '@ant-design/icons';
import { setMessages } from '@/redux/slices/app.slice';
import {
  getListAgePhysicalActivity,
  getListTypePhysicalActivity,
  getListIntensityPhysicalActivity,
  watchListPhysicalActivityByIntensity,
  getListPhysicalActivitiesByIntensity,
  deletePhysicalActivityByIntensity,
  watchPhysicalActivityLoading,
  watchAgePhysicalActivity,
  getDetailPhysicalActivityByIntensity,
  getListSuitableObjectPhysicalActivity,
  watchPhysicalActivityMessage,
  watchPaginationPhysicalActivityByIntensity,
  resetMessages
} from '@/redux/slices/regulate-level-physical-activity.slice';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { useCallback, useEffect, useState } from 'react';
import { useModal } from '@/hooks/modal.hook';
import { PhysicalActivityModal } from '@/components/modals/physical-activity-management/physical-activity-modal';
import { DeleteOutlined, EditOutlined } from '@ant-design/icons';
import IMAGE_DEFAULT from '@/assets/images/image_default.png';
import { formatDateToDDMMYYYY } from '@/utils/common';
import { PAGINATION_PARAMS } from '@/constants/pagination.constant';
import dayjs from 'dayjs';

type PageChangeParams = {
  page?: number;
  pageSize?: number;
  customFilter?: FilterPhysicalActivityByIntensity;
};


export default function ListPhysicalActivities() {
  const dispatch = useAppDispatch();
  const { openModal, closeModal } = useModal();
  const [filter, setFilter] = useState<FilterComboFood>({});
  const dataSource = useAppSelector(watchListPhysicalActivityByIntensity);
  const loading = useAppSelector(watchPhysicalActivityLoading);
  const listAgePhysicalActivity = useAppSelector(watchAgePhysicalActivity);
  const messageInfo = useAppSelector(watchPhysicalActivityMessage);
  const pagination = useAppSelector(watchPaginationPhysicalActivityByIntensity);

  const { modal } = App.useApp();

  useEffect(() => {
    dispatch(getListAgePhysicalActivity());
    dispatch(getListTypePhysicalActivity());
    dispatch(getListIntensityPhysicalActivity());
  }, [dispatch]);

  const handle = async (id?: number) => {
    if (id) {
      await dispatch(getDetailPhysicalActivityByIntensity(id));
    } else {
      await dispatch(getListSuitableObjectPhysicalActivity({}));
    }
    openModal(
      <PhysicalActivityModal
        onCancel={closeModal}
        physicalActivityID={id}
      />, { width: 1200 }
    );
  };

  const handlePageChange = useCallback(
    ({
      page = PAGINATION_PARAMS.offset,
      pageSize = PAGINATION_PARAMS.limit,
      customFilter = filter
    }: PageChangeParams) => {
      const formattedFilter = { ...customFilter };

      if (formattedFilter.START_UPDATE_DATE) {
        formattedFilter.START_UPDATE_DATE = dayjs(formattedFilter.START_UPDATE_DATE).format('YYYY-MM-DD');
      }

      if (formattedFilter.END_UPDATE_DATE) {
        formattedFilter.END_UPDATE_DATE = dayjs(formattedFilter.END_UPDATE_DATE).format('YYYY-MM-DD');
      }
      dispatch(getListPhysicalActivitiesByIntensity({
        page,
        limit: pageSize,
        customFilter: formattedFilter,
      }));
    },
    [dispatch, filter]
  );

  const handleSearch = (values?: FilterComboFood) => {
    const newFilter = {
      ...filter,
      ...values
    };
    setFilter(newFilter);
    handlePageChange({ customFilter: newFilter });
  };

  const handleDelete = async (id: number) => {
    await dispatch(deletePhysicalActivityByIntensity(id));
    handlePageChange({
      customFilter: filter
    });
  };

  const confirmDelete = (id: number) => {
    modal.confirm({
      title: 'Bạn có chắc chắn muốn xóa hoạt động thể lực này không?',
      content: (
        <em>
          Lưu ý: Dữ liệu đã xóa sẽ không thể khôi phục lại được.
        </em>
      ),
      okText: 'Xóa',
      width: 450,
      cancelText: 'Hủy',
      onOk: () => handleDelete(id)
    });
  };

  useEffect(() => {
    handlePageChange({});
  }, [handlePageChange]);

  useEffect(() => {
    if (messageInfo) {
      dispatch(
        setMessages([
          {
            type: messageInfo.type,
            message: messageInfo.message,
            description: messageInfo.description
          }
        ])
      );
      dispatch(resetMessages());
    }
  }, [dispatch, messageInfo]);

  const columns: TableColumnsType<PhysicalActivityByIntensity> = [
    {
      title: <div style={{ textAlign: 'center' }}>STT</div>,
      render: (_, __, index) => pagination ? (pagination.curPage - 1) * pagination.limitPage + index + 1 : index + 1,
      width: 50,
    },
    {
      title: <div style={{ textAlign: 'center' }}>Tên hoạt động thể lực</div>,
      dataIndex: 'NAME',
      key: 'NAME',
      width: 250,
      align: 'left'
    },
    {
      title: <div style={{ textAlign: 'center' }}>Thumbnail</div>,
      dataIndex: 'IMAGE',
      key: 'IMAGE',
      align: 'left',
      render: (record) => (
        <img
          src={record ? `${record}` : IMAGE_DEFAULT}
          style={{ width: '50px', height: '32px', objectFit: 'cover' }}
        />
      ),
    },
    {
      title: <div style={{ textAlign: 'center' }}>Độ tuổi</div>,
      dataIndex: 'AGE_RANGE_ID',
      key: 'AGE_RANGE_ID',
      width: 200,
      align: 'left',
      render: (ageID: number | string) => {
        const age = listAgePhysicalActivity.find((r) => r.ID === ageID);
        return age?.DESCRIPTION || '-';
      }
    },
    {
      title: <div style={{ textAlign: 'center' }}>Loại hình hoạt <br /> thể lực</div>,
      dataIndex: 'PHYSICAL_ACTIVITY_TYPE_NAME',
      key: 'PHYSICAL_ACTIVITY_TYPE_NAME',
      width: 200,
      align: 'left',
    },
    {
      title: <div style={{ textAlign: 'center' }}>Cường độ hoạt <br /> động thể lực</div>,
      dataIndex: 'PHYSICAL_ACTIVITY_LEVEL_NAME',
      key: 'PHYSICAL_ACTIVITY_LEVEL_NAME',
      width: 200,
      align: 'left',
    },
    {
      title: <div style={{ textAlign: 'center' }}>Hệ số MET</div>,
      dataIndex: 'MET_FACTOR',
      key: 'MET_FACTOR',
      width: 150,
      align: 'right',
    },
    {
      title: <div style={{ textAlign: 'center' }}>Đối tượng phù hợp</div>,
      dataIndex: 'HEALTH_STATUS_BMIS',
      key: 'HEALTH_STATUS_BMIS',
      width: 250,
      align: 'left',
      render: (_: any, record: any) => {
        const descriptions = record.HEALTH_STATUS_BMIS || [];

        return (
          <div>
            {descriptions.map((item: any, index: number) => (
              <div
                key={index}
                style={{
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  width: '23rem',
                }}
              >
                {item.DESCRIPTION}
              </div>
            ))}
          </div>
        );
      }
    },
    {
      title: <div style={{ textAlign: 'center' }}>Cập nhật <br /> mới nhất</div>,
      dataIndex: 'MODIFIED_DATE',
      key: 'MODIFIED_DATE',
      width: 150,
      align: 'left',
      render: (_, record) => {
        const date = record.MODIFIED_DATE ? formatDateToDDMMYYYY(record.MODIFIED_DATE) : '';
        const user = record.USERNAME_UPDATE ?? '';
        return (
          <div>
            {date}
            {user && (<> <br />{user} </>)}
          </div>
        );
      }
    },
    {
      title: <div style={{ textAlign: 'center' }}>Ngày tạo</div>,
      dataIndex: 'CREATE_DATE',
      key: 'CREATE_DATE',
      width: 150,
      align: 'left',
      render: (_, record) => {
        const date = record.CREATE_DATE ? formatDateToDDMMYYYY(record.CREATE_DATE) : '';
        const user = record.USERNAME_CREATE ?? '';
        return (
          <div>
            {date}
            {user && (<><br />{user}</>)}
          </div>
        );
      }
    },
    {
      title: <div style={{ textAlign: 'center' }}>Thao tác</div>,
      key: 'action',
      fixed: 'right',
      width: 150,
      align: 'left',
      render: (_, record) => (
        <Row gutter={[8, 10]} className='btActionStyle'>
          <Col>
            <Tooltip title='Cập nhật'>
              <ButtonAnt
                onClick={() => handle(record.ID)}
                type='primary'
                icon={<EditOutlined />}
                css={editButtonStyles}
              />
            </Tooltip>
          </Col>
          <Col>
            <Tooltip title='Xoá'>
              <ButtonAnt
                onClick={() => confirmDelete(record.ID)}
                type='primary'
                danger
                icon={<DeleteOutlined />}
              />
            </Tooltip>
          </Col>
        </Row>
      )
    }
  ];

  return (
    <div css={rootStyles}>
      <div css={boxStyles}>
        <ListPhysicalActivitiesFilter onSearch={handleSearch} />
      </div>
      <Row css={boxStyles} justify='space-between' align='middle'>
        <Col>
          <Typography.Title css={titleStyles} level={5}>
            Danh sách hoạt động thể lực
          </Typography.Title>
        </Col>
        <Button
          icon={<PlusOutlined />}
          iconPosition='start'
          style={{
            backgroundColor: '#FFFFFF',
            borderColor: '#1890FF',
            color: '#1890FF'
          }}
          onClick={() => handle()}
        >
          Tự tạo hoạt động thể lực
        </Button>
      </Row>
      <div css={tableStyle}>
        <Table<PhysicalActivityByIntensity>
          columns={columns}
          dataSource={dataSource}
          loading={loading}
          pagination={{
            showQuickJumper: false,
            showSizeChanger: true,
            current: pagination ? pagination.curPage : 1,
            pageSize: pagination ? pagination.limitPage : 10,
            total: pagination ? pagination.totalRows : dataSource.length,
            onChange: (page, pageSize) => {
              handlePageChange({ page, pageSize });
            }
          }}
        />
      </div>
    </div>
  );
}

const rootStyles = css`
  background: var(--gray-soft-color);
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const tableStyle = css`
  .ant-spin-container {
    padding: 4px !important;
  }

  background: var(--white-color);
  .ant-table-row .ant-table-cell {
    padding: 4px 8px !important;
  }
  .ant-table-thead .ant-table-cell {
    background-color: var(--blue-dark-color);
    color: var(--white-color);
  }
  .ant-table-cell::before {
    display: none !important;
  }

  .ant-table-wrapper .ant-table-cell {
    padding: 8px 8px !important;
  }
  
  .btActionStyle {
    display: flex;
    justify-content: center;
    align-items: center;
  }
  
  .ant-table-wrapper .ant-table-container table>thead>tr:first-child >*:first-child {
    border-start-start-radius: 0px !important;
  }
  
  .ant-table-wrapper .ant-table-container table>thead>tr:first-child >*:last-child {
    border-start-end-radius: 0px !important;
  }
`;

const boxStyles = css`
  width: 100%;
  padding: 1rem;
  background: var(--white-color);
  box-shadow: 0.02rem 0.04rem 0.2rem var(--gray-light-color);
  &:nth-of-type(2) {
    padding: 0.8rem 1rem;
  }
  margin-top: 0.5rem;
`;

const titleStyles = css`
  margin-bottom: 0 !important;
  line-height: 3.2rem !important;
`;

const editButtonStyles = css`
  background-color: var(--yellow-light-color) !important;
  color: var(--white-color);
`;